'use client';
import { useEffect, useState } from 'react';

import { useAtom, useAtomValue } from 'jotai';

import { localeAtom } from '@core/Atoms/Locale/Locale.atom';
import type { CampaignOfferProps } from '@core/Entities/OfferPromoContent/OfferPromoContent.entity';
import type { Vehicle } from '@core/Entities/Vehicle/Vehicle.entity';
import { isEmptyRichText } from '@core/Utils/Html/IsEmptyRichText';
import { getPromotions } from '@core/Utils/Promotions/getPromotions';
import { isD2cModelVsp } from '@core/Utils/Vehicle/isD2cModelVsp';
import { Promotion } from '@gds/Icons';
import { Typography } from '@gds/Typography/Typography';
import { flagsAtom } from '@growthBookExperimentation/Atoms/Flags.atom';
import { MissingKenticoContent } from '@kentico/Components/MissingKenticoContent';
import { getKenticoContent } from '@kentico/GetKenticoContent';
import { useModal } from '@modal/Hooks/UseModal';

import { campaignOfferStorageAtom, offerModalWithStorageAtom } from './Atoms/OfferModalAtom';
import { OfferModal } from './OfferModal';
import { defaultOfferModalMessages as messages } from './OfferModal.messages';
import { OfferLayoutSkeleton } from './OfferModalSkeleton';
import { PromotionsModal } from './PromotionsModal';

import styles from './OfferModal.module.css';

interface Props {
  vehicle: Vehicle | null;
}

export const OfferModalLayout = ({ vehicle }: Props) => {
  const { openModal } = useModal();
  const locale = useAtomValue(localeAtom);
  const flags = useAtomValue(flagsAtom);
  const { count, hasPromotions, promotions, isFreeDelivery } = getPromotions(vehicle);
  const canShowFreeDelivery = isFreeDelivery || flags.freeDeliveryOffer;
  const isD2C = isD2cModelVsp(vehicle);
  const isEcom = vehicle?.ecom.isEcom;
  const isD2CorEcom = isD2C || isEcom;
  const [offerModalData, setOfferModalData] = useAtom(offerModalWithStorageAtom);
  const [filteredOffers, setFilteredOffers] = useAtom(campaignOfferStorageAtom);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [dynamicCount, setDynamicCount] = useState<number>(0);

  const fetchOfferData = async () => {
    try {
      const defaultRes = await getKenticoContent<any>('pdp_modal_campaign', 2, locale);

      const campaignRes: CampaignOfferProps[] | null = await getKenticoContent<CampaignOfferProps>(
        'un_component_campaign_offers',
        2,
        locale,
        undefined,
        true,
      );

      // Filter campaign offers based on vehicle eligible products codes
      const eligibleOffers = campaignRes
        ? campaignRes.filter((offer: CampaignOfferProps) =>
            vehicle?.eligibleProducts?.includes(offer.campaignCode),
          )
        : [];

      const count = eligibleOffers.reduce((acc: number, offer: CampaignOfferProps) => {
        let newAcc = acc;
        if (!isEmptyRichText(offer.titleCampaignprimary)) newAcc++;
        if (!isEmptyRichText(offer.titleCampaignsecondary)) newAcc++;
        return newAcc;
      }, 2);

      setDynamicCount(count);
      setOfferModalData(defaultRes || []);
      setFilteredOffers(eligibleOffers);
      setLoading(false);
    } catch (_error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasPromotions) {
      setDynamicCount(count);
    } else {
      fetchOfferData();
    }
  }, []);

  const openOfferModal = () => {
    let modalComponent = null;

    if (hasPromotions) {
      modalComponent = <PromotionsModal promotions={promotions} title={messages.modalTitle} />;
    }

    if (offerModalData && filteredOffers && !isLoading && !hasPromotions) {
      modalComponent = (
        <OfferModal offerModalContent={offerModalData} campaignOffers={filteredOffers} />
      );
    } else if (!isLoading && !hasPromotions) {
      modalComponent = (
        <MissingKenticoContent identifier="pdp_modal_campaign" contentType="CampaignOfferProps[]" />
      );
    }

    openModal(modalComponent, {
      header: messages?.campaignTitle,
      modalClassName: styles.modalContainer,
      modalBodyClassName: styles.modalBody,
    });
  };

  if (!hasPromotions && isLoading) {
    return <OfferLayoutSkeleton />;
  }

  if ((!hasPromotions && !isD2CorEcom) || count === 0) return null;

  return (
    <div className={styles.campaignSelectorWrapper} onClick={openOfferModal}>
      <Promotion />

      <div className={styles.layoutBody}>
        {canShowFreeDelivery ? (
          <span>
            <Typography Component="span" variant="body2">
              {dynamicCount} {messages?.campaignDelivery}
            </Typography>{' '}
          </span>
        ) : (
          <span>
            <Typography Component="span" variant="body2">
              {dynamicCount} {messages?.campaignCount}
            </Typography>
            <Typography Component="span" variant="body4">
              {messages?.campaignLayoutOneTwo}
            </Typography>{' '}
          </span>
        )}

        <Typography Component="span" variant="body4" className={styles.layoutText}>
          {messages?.findMore}
        </Typography>
        <Typography Component="span" variant="body2" className={styles.layoutTextMobile}>
          {messages?.findMoreMobile}
        </Typography>
      </div>
    </div>
  );
};
